import {
	Avatar,
	AvatarFallback,
	AvatarImage,
} from "@client/components/ui/avatar";
import { Button } from "@client/components/ui/button";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuGroup,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@client/components/ui/dropdown-menu";
import { authClient } from "@client/lib/auth-client";
import { captureClientException } from "@client/lib/sentry";
import { useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
	CreditCard,
	FileText,
	Key,
	LayoutDashboard,
	LogOutIcon,
	Receipt,
	User,
	UserCircleIcon,
} from "lucide-react";
import { toast } from "sonner";

export function UserMenu() {
	const { data: session } = authClient.useSession();
	// Called before the early return below — hooks must not be conditional
	const queryClient = useQueryClient();

	if (!session?.user) {
		return (
			<Button variant="outline" size="sm" asChild>
				<Link to="/">Login</Link>
			</Button>
		);
	}

	// Get user initials for avatar fallback
	const getUserInitials = () => {
		if (!session.user?.name) return "U";
		return session.user.name
			.split(" ")
			.map((name) => name[0])
			.join("")
			.toUpperCase()
			.substring(0, 2);
	};

	const handleSignOut = async () => {
		try {
			await authClient.signOut({
				fetchOptions: {
					onSuccess: () => {
						// Drop every cached query immediately — the next account must
						// never read this one's sites, credits or invoices.
						queryClient.clear();
						toast.success("Session terminated");
					},
					onError: (error) => {
						console.error("Sign out error:", error);
						toast.error("Failed to terminate session");
					},
				},
			});
		} catch (error) {
			console.error("Sign out exception:", error);
			toast.error("An error occurred during sign out");

			captureClientException(error as Error, {
				tags: {
					operation: "signOut",
				},
			});
		}
	};

	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<Button
					variant="ghost"
					className="relative h-8 w-8 border-2 border-border/60 p-0"
				>
					{session.user?.image ? (
						<Avatar className="h-7 w-7">
							<AvatarImage
								src={session.user.image}
								alt={session.user?.name ?? "User"}
							/>
							<AvatarFallback className="bg-primary/20 font-mono text-xs">
								{getUserInitials()}
							</AvatarFallback>
						</Avatar>
					) : (
						<User className="h-4 w-4 text-muted-foreground" />
					)}
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuContent
				className="w-56 border-2 border-border/60"
				align="end"
			>
				<DropdownMenuLabel className="p-0 font-normal">
					<div className="flex items-center gap-2 px-2 py-2 text-left">
						<div className="flex h-8 w-8 items-center justify-center border border-border/40 bg-muted/30">
							<span className="font-mono text-primary text-xs">
								{getUserInitials()}
							</span>
						</div>
						<div className="grid flex-1 text-left leading-tight">
							<span className="truncate font-medium font-mono text-sm">
								{session.user?.name ?? "User"}
							</span>
							<span className="truncate font-mono text-muted-foreground text-xs">
								{session.user?.email}
							</span>
						</div>
					</div>
				</DropdownMenuLabel>
				<DropdownMenuSeparator />
				<DropdownMenuGroup>
					<DropdownMenuItem asChild className="font-mono text-xs">
						<Link to="/dashboard">
							<LayoutDashboard className="mr-2 size-3" />
							<span className="text-primary/60">&gt;</span> DASHBOARD
						</Link>
					</DropdownMenuItem>
					<DropdownMenuItem asChild className="font-mono text-xs">
						<Link to="/settings/account">
							<UserCircleIcon className="mr-2 size-3" />
							<span className="text-primary/60">&gt;</span> ACCOUNT
						</Link>
					</DropdownMenuItem>
					<DropdownMenuItem asChild className="font-mono text-xs">
						<Link to="/settings/billing">
							<CreditCard className="mr-2 size-3" />
							<span className="text-primary/60">&gt;</span> BILLING
						</Link>
					</DropdownMenuItem>
					<DropdownMenuItem asChild className="font-mono text-xs">
						<Link to="/settings/invoices">
							<Receipt className="mr-2 size-3" />
							<span className="text-primary/60">&gt;</span> INVOICES
						</Link>
					</DropdownMenuItem>
					<DropdownMenuItem asChild className="font-mono text-xs">
						<Link to="/settings/tax">
							<FileText className="mr-2 size-3" />
							<span className="text-primary/60">&gt;</span> TAX_INFO
						</Link>
					</DropdownMenuItem>
					<DropdownMenuItem asChild className="font-mono text-xs">
						<Link to="/settings/api-keys">
							<Key className="mr-2 size-3" />
							<span className="text-primary/60">&gt;</span> API_KEYS
						</Link>
					</DropdownMenuItem>
				</DropdownMenuGroup>
				<DropdownMenuSeparator />
				<DropdownMenuItem
					onClick={handleSignOut}
					className="font-mono text-red-500 text-xs"
				>
					<LogOutIcon className="mr-2 size-3" />
					<span className="text-red-500/60">&gt;</span> LOGOUT
				</DropdownMenuItem>
			</DropdownMenuContent>
		</DropdownMenu>
	);
}
